import { useEffect, useState } from 'react'
import type { PuzzleRendererProps } from '@/game/PuzzleRenderer'
import { PuzzleRenderer } from '@/game/PuzzleRenderer'
import { MediaGallery } from '@/game/MediaGallery'
import type { Media, PublicPuzzle, PuzzleType } from '@/lib/api'
import { useServerClock } from '@/game/useServerClock'

type Config = { revealSeconds?: number; question?: { type: PuzzleType; publicConfig: Record<string, unknown> } }
type State = {
  phase?: 'idle' | 'reveal' | 'answer'
  revealSeconds?: number
  revealEndsAt?: string | null
  reveal?: { text?: string; media?: Media[] }
}

/**
 * Reveal-then-answer. The reveal window is timed by the server; this component
 * only counts down and asks for a resync when the server deadline passes.
 */
export function MemoryPuzzle(props: PuzzleRendererProps) {
  const { puzzle, onAction, onResync, disabled, serverTime } = props
  const config = puzzle.publicConfig as Config
  const state = puzzle.state as State
  const now = useServerClock(serverTime)
  const [starting, setStarting] = useState(false)
  const endsAt = state.revealEndsAt ? Date.parse(state.revealEndsAt) : null
  const remaining = endsAt ? Math.max(0, Math.ceil((endsAt - now) / 1000)) : null

  useEffect(() => {
    if (state.phase === 'reveal' && remaining === 0) void onResync()
  }, [state.phase, remaining, onResync])

  if (state.phase === 'idle' || !state.phase) {
    return (
      <div className="puzzle-form">
        <p className="muted">
          You will have {state.revealSeconds ?? config.revealSeconds} seconds to memorise what appears. The timer is
          controlled by the server and cannot be restarted.
        </p>
        <button
          type="button"
          className="primary"
          disabled={disabled || starting}
          onClick={async () => {
            setStarting(true)
            try {
              await onAction('start')
            } finally {
              setStarting(false)
            }
          }}
        >
          {starting ? 'Starting…' : 'Show it to me'}
        </button>
      </div>
    )
  }

  if (state.phase === 'reveal') {
    return (
      <div className="puzzle-form reveal" aria-live="polite">
        <p className="countdown">{remaining ?? ''}s</p>
        {state.reveal?.text && <p className="reveal-text">{state.reveal.text}</p>}
        {state.reveal?.media && <MediaGallery media={state.reveal.media} />}
      </div>
    )
  }

  // Answer phase: render the embedded question with the normal renderer.
  const question: PublicPuzzle | null = config.question
    ? { ...puzzle, type: config.question.type, publicConfig: config.question.publicConfig, state: {} }
    : null
  if (!question) return <p className="status-err">This memory puzzle has no question configured.</p>
  return (
    <div>
      <p className="muted">Time&apos;s up. What did you see?</p>
      <PuzzleRenderer {...props} puzzle={question} />
    </div>
  )
}
